import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Axios } from "../../Services/Axios";
import { useFormik } from "formik";
import ReactPaginate from "react-paginate";
import { Storage } from "../../Services/Storage";
import { fetchPermissions } from "../../Components/sidebar";

export const Verification = () => {
  const [formValues, setFormValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [itemOffset, setItemOffset] = useState(0);
  const [open, setOpen] = useState(false);

  const pageCount = Math.ceil(formValues.length / 10);
  const id = Storage.getItem("user").id;

  useEffect(() => {
    const endOffset = itemOffset + 10;
    setFiltered(formValues.slice(itemOffset, endOffset));
  }, [itemOffset, formValues]);

  useEffect(() => {
    fetchPermissions(id)
      .then((res) => {
        const permission = res.find((item) => item.title === "Verification");
        setPermissions(permission || {});
      })
      .catch(() => toast.error("Uncaught error"));
    GetAll();
  }, []);

  const GetAll = () => {
    setLoading(true);
    Axios.get("/StudentDocumentVerification/GetAll").then(
      (res) => {
        setLoading(false);
        setFormValues(res.data);
      },
      () => {
        setLoading(false);
        toast.error("Failed to load records.");
      }
    );
  };

  const handlePage = (event) => {
    const newOffset = (event.selected * 10) % formValues.length;
    setItemOffset(newOffset);
  };

  const toInputDate = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.substring(0, 10);
  };

  const initialValues = {
    id: "",
    studentName: "",
    studentId: "",
    certificateNo: "",
    psNo: "",
    issueDate: "",
    verificationDate: "",
  };

  const validationSchema = Yup.object().shape({
    studentName: Yup.string().required("Student Name is required"),
    studentId: Yup.string().required("Student ID is required"),
    certificateNo: Yup.string().required("Certificate No is required"),
    psNo: Yup.string().required("PS No / SR No is required"),
    issueDate: Yup.string().required("Issue Date is required"),
    verificationDate: Yup.string().required("Verification Date is required"),
  });

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitLoading(true);
    const payload = {
      ...values,
      issueDate: new Date(values.issueDate).toISOString(),
      verificationDate: new Date(values.verificationDate).toISOString(),
    };

    if (values.id) {
      Axios.put("/StudentDocumentVerification/Update", payload).then(
        () => {
          setSubmitLoading(false);
          resetForm();
          setOpen(false);
          GetAll();
          toast.success("Record updated successfully");
        },
        () => {
          setSubmitLoading(false);
          toast.error("Something went wrong...");
        }
      );
    } else {
      const { id: _id, ...addPayload } = payload;
      Axios.post("/StudentDocumentVerification/Add", addPayload).then(
        () => {
          setSubmitLoading(false);
          resetForm();
          setOpen(false);
          GetAll();
          toast.success("Record added successfully");
        },
        () => {
          setSubmitLoading(false);
          toast.error("Something went wrong...");
        }
      );
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const openModal = () => {
    if (open) {
      formik.resetForm();
    }
    setOpen(!open);
  };

  const Edit = (record) => {
    formik.setValues({
      id: record.id,
      studentName: record.studentName,
      studentId: record.studentId,
      certificateNo: record.certificateNo,
      psNo: record.psNo,
      issueDate: toInputDate(record.issueDate),
      verificationDate: toInputDate(record.verificationDate),
    });
    setOpen(true);
  };

  const Delete = (recordId) => {
    Axios.delete(`/StudentDocumentVerification/Remove?id=${recordId}`).then(
      () => {
        setFormValues((prev) => prev.filter((x) => x.id !== recordId));
        toast.success("Record deleted successfully");
      },
      () => {
        toast.error("Something went wrong...");
      }
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB");
  };

  return (
    <>
      <div className="pageTitle mx-4 my-4">
        <div>
          <h3>Student Document Verification</h3>
        </div>
        {permissions.canAdd && (
          <button className="btn btntheme" onClick={openModal}>
            Add Verification
          </button>
        )}
      </div>

      <div className="p-4">
        <div className="table border border-gray-200 rounded">
          <div className="tablebody">
            {loading && <p className="p-4">Loading...</p>}
            {!loading && formValues.length === 0 && (
              <p className="p-4">No data found.</p>
            )}
            {!loading && formValues.length > 0 && (
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-[#ededed]">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Student Name
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Student ID
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Certificate No
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      PS No / SR No
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Issue Date
                    </th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                      Verification Date
                    </th>
                    {(permissions.canUpdate || permissions.canDelete) && (
                      <th className="text-right relative py-3.5 pl-3 pr-4 sm:pr-6 text-sm font-semibold text-gray-900">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filtered.map((e, i) => (
                    <tr key={i}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                        {e.studentName}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {e.studentId}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {e.certificateNo}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {e.psNo}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {formatDate(e.issueDate)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {formatDate(e.verificationDate)}
                      </td>
                      {(permissions.canUpdate || permissions.canDelete) && (
                        <td className="whitespace-nowrap px-3 py-4 text-right">
                          {permissions.canUpdate && (
                            <span className="mx-2 cursor-pointer" onClick={() => Edit(e)}>
                              <i className="fa-solid fa-pen-to-square"></i>
                            </span>
                          )}
                          {permissions.canDelete && (
                            <span className="mx-2 cursor-pointer" onClick={() => Delete(e.id)}>
                              <i className="fa-solid fa-trash-can"></i>
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {!loading && formValues.length > 0 && (
          <div className="mt-4">
            <ReactPaginate
              breakLabel="..."
              nextLabel="&raquo;"
              onPageChange={handlePage}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="&laquo;"
              renderOnZeroPageCount={null}
              containerClassName="mars-pagination"
              pageClassName="mars-page"
              nextClassName="mars-page"
              previousClassName="mars-page"
              activeClassName="active"
            />
          </div>
        )}
      </div>

      {open && (
        <div className="popup_overlay">
          <div className="popup_notify" style={{ maxWidth: "520px", width: "100%" }}>
            <div className="popup_header">
              <h4>{formik.values.id ? "Edit Verification" : "Add Verification"}</h4>
              <button className="btn" onClick={openModal} type="button">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="popup_body">
              <form onSubmit={formik.handleSubmit}>
                <label htmlFor="studentName">Student Name</label>
                <input
                  type="text"
                  id="studentName"
                  name="studentName"
                  className="input"
                  placeholder="eg. John Doe"
                  value={formik.values.studentName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <small className="error">
                  {formik.touched.studentName && formik.errors.studentName}
                </small>

                <label htmlFor="studentId">Student ID</label>
                <input
                  type="text"
                  id="studentId"
                  name="studentId"
                  className="input"
                  placeholder="eg. STU-001"
                  value={formik.values.studentId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <small className="error">
                  {formik.touched.studentId && formik.errors.studentId}
                </small>

                <label htmlFor="certificateNo">Certificate No</label>
                <input
                  type="text"
                  id="certificateNo"
                  name="certificateNo"
                  className="input"
                  placeholder="eg. CERT-2024-001"
                  value={formik.values.certificateNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <small className="error">
                  {formik.touched.certificateNo && formik.errors.certificateNo}
                </small>

                <label htmlFor="psNo">PS No / SR No</label>
                <input
                  type="text"
                  id="psNo"
                  name="psNo"
                  className="input"
                  placeholder="eg. PS-001 or SR-001"
                  value={formik.values.psNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <small className="error">
                  {formik.touched.psNo && formik.errors.psNo}
                </small>

                <label htmlFor="issueDate">Issue Date</label>
                <input
                  type="date"
                  id="issueDate"
                  name="issueDate"
                  className="input"
                  value={formik.values.issueDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <small className="error">
                  {formik.touched.issueDate && formik.errors.issueDate}
                </small>

                <label htmlFor="verificationDate">Verification Date</label>
                <input
                  type="date"
                  id="verificationDate"
                  name="verificationDate"
                  className="input"
                  value={formik.values.verificationDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <small className="error">
                  {formik.touched.verificationDate && formik.errors.verificationDate}
                </small>

                <div className="popup_footer">
                  <button
                    className="btn btnclose"
                    onClick={openModal}
                    type="button"
                    disabled={submitLoading}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn btntheme"
                    disabled={submitLoading}
                  >
                    {submitLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};